/**
 * WeatherWidgetPresenter - Main weather widget with tab navigation
 *
 * This is the primary federated component for JHP. It manages tab state
 * internally and renders the appropriate day/week view based on selection.
 *
 * This component is JHP-specific - it wraps the day/week presenters with
 * tab navigation and handles locale synchronization with JHP's i18n.
 *
 * Note: This is a class component (not functional) because we need to set
 * the locale in the constructor BEFORE the first render. With useEffect,
 * the locale would only be set after the first render, causing a flash
 * of wrong translations.
 */

import * as React from 'react'

import { classList } from 'react-classlist-helper'

import { IWeatherBarForecast } from '~/client/src/shared/components/WeatherHourlyWidget/WeatherBarPresenter'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { setJHPLocale } from '~/client/src/shared/utils/jhpLocale'

import type {
  IJHPHourlyForecast,
  IJHPWeatherForecast,
  IWeatherWidgetPresenterProps,
} from './types'
import {
  addDays,
  formatDateDisplay,
  formatMonthDayDisplay,
  formatTimeDisplay,
  formatWeekdayDisplay,
  getStartOfDay,
  isSameDay,
} from './utils'
import WeatherDayPresenter, { IWeatherDayForecast } from './WeatherDayPresenter'
import WeatherWeekPresenter, {
  IWeatherWeekForecast,
} from './WeatherWeekPresenter'

import './WeatherWidgetPresenter.scss'

enum ForecastPeriod {
  Today = 0,
  Tomorrow = 1,
  Week = 2,
}

const PRESET_BUTTONS = [
  { key: 'today', shift: ForecastPeriod.Today },
  { key: 'tomorrow', shift: ForecastPeriod.Tomorrow },
  { key: 'fiveDayForecast', shift: ForecastPeriod.Week },
]

/**
 * Transform JHP hourly forecast to presenter format
 */
function transformHourlyForecasts(
  hourlyForecasts: IJHPHourlyForecast[] | undefined,
): IWeatherBarForecast[] {
  if (!hourlyForecasts) return []
  return hourlyForecasts.map(h => ({
    time: h.time,
    temperature: h.temperature,
    weatherIconCode: h.weatherIconCode,
    windSpeed: h.windSpeed,
    windDegree: h.windDegree,
    probabilityOfPrecipitation: h.probabilityOfPrecipitation,
    amountOfPrecipitation: h.amountOfPrecipitation,
  }))
}

/**
 * Transform JHP forecast to day presenter format
 */
function transformDayForecast(
  forecast: IJHPWeatherForecast,
): IWeatherDayForecast {
  return {
    temperature: forecast.temperature,
    minTemperature: forecast.minTemperature,
    maxTemperature: forecast.maxTemperature,
    humidity: forecast.humidity,
    windSpeed: forecast.windSpeed,
    windDegree: forecast.windDegree,
    probabilityOfPrecipitation: forecast.probabilityOfPrecipitation,
    amountOfPrecipitation: forecast.amountOfPrecipitation,
    weatherIconCode: forecast.weatherIconCode,
    weatherDescription: forecast.weatherDescription,
  }
}

/**
 * Transform JHP forecasts to week presenter format
 */
function transformWeekForecasts(
  forecasts: IJHPWeatherForecast[],
  today: number,
  fiveDaysFromToday: number,
  timezone: string,
): IWeatherWeekForecast[] {
  return forecasts
    .filter(f => {
      const forecastStart = getStartOfDay(f.date, timezone)
      return forecastStart > today && forecastStart <= fiveDaysFromToday
    })
    .sort((a, b) => a.date - b.date)
    .map(f => ({
      date: f.date,
      temperature: f.temperature,
      weatherIconCode: f.weatherIconCode,
      windSpeed: f.windSpeed,
      windDegree: f.windDegree,
      probabilityOfPrecipitation: f.probabilityOfPrecipitation,
      amountOfPrecipitation: f.amountOfPrecipitation,
      weatherDescription: f.weatherDescription,
    }))
}

interface IState {
  currentView: ForecastPeriod
  /** Tracks if user has manually selected a view (prevents auto-switching) */
  userSelectedView: boolean
}

export default class WeatherWidgetPresenter extends React.Component<
  IWeatherWidgetPresenterProps,
  IState
> {
  public state: IState = {
    currentView: ForecastPeriod.Today,
    userSelectedView: false,
  }

  public constructor(props: IWeatherWidgetPresenterProps) {
    super(props)

    // Set locale in constructor so translations are correct on first render
    setJHPLocale(props.locale)
  }

  public componentDidMount(): void {
    // Set initial view based on available data
    this.selectBestAvailableView()
  }

  public componentDidUpdate(prevProps: IWeatherWidgetPresenterProps): void {
    // Update locale if it changes
    if (prevProps.locale !== this.props.locale) {
      setJHPLocale(this.props.locale)
      this.forceUpdate()
    }

    // If forecasts changed and user hasn't manually selected, re-evaluate best view
    if (
      prevProps.forecasts !== this.props.forecasts &&
      !this.state.userSelectedView
    ) {
      this.selectBestAvailableView()
    }
  }

  /**
   * Determines the best available forecast view based on data availability.
   * Priority: Today > Tomorrow > Week
   */
  private getBestAvailableView(): ForecastPeriod {
    const { forecasts, timezone } = this.props
    if (forecasts.length === 0) return ForecastPeriod.Today

    const now = Date.now()
    const todayStart = getStartOfDay(now, timezone)
    const tomorrowStart = addDays(todayStart, 1)

    const hasTodayForecast = forecasts.some(f =>
      isSameDay(f.date, now, timezone),
    )
    const hasTomorrowForecast = forecasts.some(f =>
      isSameDay(f.date, tomorrowStart, timezone),
    )

    if (hasTodayForecast) return ForecastPeriod.Today
    if (hasTomorrowForecast) return ForecastPeriod.Tomorrow
    return ForecastPeriod.Week
  }

  /**
   * Sets the view to the best available option if current view has no data.
   *
   * Important: We only switch away from Today if we're confident today's data
   * won't arrive. If today's forecast is missing, we wait for subsequent updates
   * that might include it (data arrives incrementally on remount).
   */
  private selectBestAvailableView(): void {
    const { currentView } = this.state
    const { forecasts, timezone } = this.props

    if (forecasts.length === 0) return

    const now = Date.now()
    const todayStart = getStartOfDay(now, timezone)
    const tomorrowStart = addDays(todayStart, 1)

    const hasTodayForecast = forecasts.some(f =>
      isSameDay(f.date, now, timezone),
    )
    const hasTomorrowForecast = forecasts.some(f =>
      isSameDay(f.date, tomorrowStart, timezone),
    )

    // If we're on Today view and today's forecast is missing, wait for more data
    if (currentView === ForecastPeriod.Today && !hasTodayForecast) {
      return
    }

    // Check if current view has data
    const currentViewHasData =
      (currentView === ForecastPeriod.Today && hasTodayForecast) ||
      (currentView === ForecastPeriod.Tomorrow && hasTomorrowForecast) ||
      currentView === ForecastPeriod.Week

    if (!currentViewHasData) {
      this.setState({ currentView: this.getBestAvailableView() })
    }
  }

  private handleTabClick = (shift: ForecastPeriod) => (): void => {
    this.setState({ currentView: shift, userSelectedView: true })
  }

  private formatTime = (timestamp: number): [string, string] => {
    return formatTimeDisplay(timestamp, this.props.timezone)
  }

  private formatWeekday = (timestamp: number): string => {
    return formatWeekdayDisplay(timestamp, this.props.timezone)
  }

  private formatMonthDay = (timestamp: number): string => {
    return formatMonthDayDisplay(timestamp, this.props.timezone)
  }

  public render(): JSX.Element | null {
    const {
      forecasts,
      projectAddress,
      timezone,
      units,
      className,
      compactH,
      compactV,
    } = this.props
    const { currentView } = this.state

    // If no data available, show nothing
    if (forecasts.length === 0) {
      return null
    }

    // Find today's and tomorrow's forecasts
    const now = Date.now()
    const todayStart = getStartOfDay(now, timezone)
    const tomorrowStart = addDays(todayStart, 1)
    const fiveDaysFromToday = addDays(todayStart, 5)

    const todayForecast = forecasts.find(f => isSameDay(f.date, now, timezone))
    const tomorrowForecast = forecasts.find(f =>
      isSameDay(f.date, tomorrowStart, timezone),
    )

    // Get the forecast and hourly data based on current view
    let selectedForecast: IJHPWeatherForecast | undefined
    let hourlyForecasts: IJHPHourlyForecast[] = []
    let displayDate: number = now

    switch (currentView) {
      case ForecastPeriod.Today:
        selectedForecast = todayForecast
        hourlyForecasts = todayForecast?.hourlyForecasts || []
        displayDate = now
        break
      case ForecastPeriod.Tomorrow:
        selectedForecast = tomorrowForecast
        hourlyForecasts = tomorrowForecast?.hourlyForecasts || []
        displayDate = tomorrowStart
        break
      case ForecastPeriod.Week:
        // Week view doesn't need a single forecast
        break
    }

    // Get translations directly from Localization (locale was set in constructor/componentDidUpdate)
    const translationKeys: Record<string, string> = {
      today: Localization.translator.today,
      tomorrow: Localization.translator.tomorrow,
      fiveDayForecast: Localization.translator.fiveDayForecast,
    }

    // Format the date range for week view
    const formattedDateRange = `${this.formatMonthDay(
      tomorrowStart,
    )} - ${formatDateDisplay(fiveDaysFromToday, timezone)}`

    return (
      <div
        className={classList({
          'jhp-weather-widget-presenter': true,
          [className || '']: !!className,
        })}
      >
        <div
          className={classList({
            'jhp-weather-widget-presenter__content col': true,
            'pt16 pb8 gap-8': !compactV,
            px16: !compactH,
            px4: compactH,
          })}
        >
          <div className="row full-width full-height x-start relative generic-tabs-container no-select">
            {PRESET_BUTTONS.map(({ key, shift }) => (
              <div
                key={key}
                className={classList({
                  'inline-block vertical-align-middle bb-light-grey': true,
                  h50: !compactV,
                  h36: compactV,
                })}
              >
                <div
                  className={classList({
                    'row x-center y-center pointer no-flex tab-item': true,
                    active: shift === currentView,
                  })}
                  onClick={this.handleTabClick(shift)}
                >
                  {translationKeys[key]}
                </div>
              </div>
            ))}
          </div>

          {currentView === ForecastPeriod.Week ? (
            <WeatherWeekPresenter
              forecasts={transformWeekForecasts(
                forecasts,
                todayStart,
                fiveDaysFromToday,
                timezone,
              )}
              projectAddress={projectAddress}
              units={units}
              formattedDateRange={formattedDateRange}
              formatWeekday={this.formatWeekday}
              formatMonthDay={this.formatMonthDay}
              compactH={compactH}
              compactV={compactV}
            />
          ) : (
            selectedForecast && (
              <WeatherDayPresenter
                forecast={transformDayForecast(selectedForecast)}
                hourlyForecasts={transformHourlyForecasts(hourlyForecasts)}
                projectAddress={projectAddress}
                units={units}
                formattedDate={formatDateDisplay(displayDate, timezone)}
                formatTime={this.formatTime}
                compactH={compactH}
                compactV={compactV}
              />
            )
          )}
        </div>
      </div>
    )
  }
}
